import React, { useEffect } from 'react';
import { theme } from '../styles/theme';

// A fixed-size, fixed-seed texture: no animated noise or element overlays.
const GRAIN = `url("data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="193" height="193" viewBox="0 0 193 193"><filter id="grain"><feTurbulence type="fractalNoise" baseFrequency=".85" numOctaves="3" seed="17" stitchTiles="stitch"/><feColorMatrix type="saturate" values="0"/><feComponentTransfer><feFuncA type="linear" slope=".05"/></feComponentTransfer></filter><rect width="100%" height="100%" filter="url(#grain)"/></svg>')}")`;


// Only existing small brand surfaces; never imagery or carousel timelines.
const SURFACES = [
  '.header-app', '.hero-download', '.hero-headline-accent',
  '.features-clubs-heading h2 span', '.features-club-brand',
  '.testimonials-rail-brand', '.pricing-selector button',
  '.pricing-card-pro', '.pricing-card-pro .pricing-download',
  '.match-footer__brand', '.match-footer__app-mark', '.match-footer__cta',
].join(', ');
const SECTION_PAUSE = '.features-clubs-playback, .testimonials-rail-controls button, .hiw-playback';

export const HomePagePresentation: React.FC<{
  root: React.RefObject<HTMLDivElement | null>;
}> = ({ root }) => {

  useEffect(() => {
    const container = root.current;
    if (!container) return;
    const sections = Array.from(container.querySelectorAll<HTMLElement>('main > section, .site-header, .match-footer'));
    const visible = new Set<HTMLElement>();
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const surfaces = new Set<HTMLElement>();
    const discover = () => container.querySelectorAll<HTMLElement>(SURFACES).forEach(surface => {
      if (surfaces.has(surface)) return;
      surfaces.add(surface);
      const kind = surface.matches('.pricing-card-pro') ? 'border'
        : surface.matches('.hero-headline-accent, .features-clubs-heading h2 span') ? 'text'
        : surface.matches('.features-club-brand, .testimonials-rail-brand, .match-footer__brand, .match-footer__app-mark') ? 'full' : 'cta';
      surface.setAttribute('data-home-gradient', kind);
      // Stable phase offsets prevent neighbouring surfaces moving in lockstep.
      surface.style.setProperty('--home-flow-delay', `${-(surfaces.size % 9) * .73}s`);
    });
    discover();
    const sync = () => sections.forEach(section => {
      const localPause = section.querySelector(SECTION_PAUSE)?.getAttribute('aria-pressed') === 'true';
      section.style.setProperty('--home-gradient-state',
        document.hidden || motion.matches || !visible.has(section) || localPause ? 'paused' : 'running');
    });
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) visible.add(entry.target as HTMLElement);
        else visible.delete(entry.target as HTMLElement);
      });
      sync();
    });
    sections.forEach(section => { if (observer) observer.observe(section); else visible.add(section); });
    // Never observe the per-frame style mutations used by the carousels.
    const mutations = new MutationObserver(records => {
      if (records.some(record => record.type === 'childList')) discover();
      sync();
    });
    mutations.observe(container, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-pressed'] });
    document.addEventListener('visibilitychange', sync);
    motion.addEventListener('change', sync);
    sync();
    return () => {
      observer?.disconnect(); mutations.disconnect();
      document.removeEventListener('visibilitychange', sync);
      motion.removeEventListener('change', sync);
      surfaces.forEach(surface => {
        surface.removeAttribute('data-home-gradient');
        surface.style.removeProperty('--home-flow-delay');
      });
      sections.forEach(section => section.style.removeProperty('--home-gradient-state'));
    };
  }, [root]);

  return <style>{`
    /* Keep whole homepage sections out of the global entrance animation.
       Nested carousel and decorative animations keep their own timelines. */
    .homepage-presentation > main > section {
      opacity: 1;
      transform: none;
      animation: none;
    }
    .homepage-presentation .hero-particles { display: none; }
    /* Local rendering isolation: keep decorative gradients at a static phase. */
    .homepage-presentation .matchday-hero .hero-bg-orb { animation-play-state: paused; }
    @property --homepage-flow-angle {
      syntax: '<angle>';
      inherits: false;
      initial-value: 0deg;
    }
    .homepage-presentation [data-home-gradient] {
      animation: homepageColorTravel 8s ease-in-out infinite !important;
      animation-delay: var(--home-flow-delay, 0s) !important;
      animation-play-state: paused !important;
    }
    .homepage-presentation [data-home-gradient="full"] {
      background-image: ${GRAIN},
        radial-gradient(ellipse at 35% 40%, ${theme.colors.primary} 0%, transparent 40.3%),
        radial-gradient(ellipse at 65% 55%, ${theme.colors.secondary} 0%, transparent 42.25%),
        conic-gradient(from var(--homepage-flow-angle), ${theme.colors.primary}, ${theme.colors.secondary}, ${theme.colors.primary}, ${theme.colors.secondary}, ${theme.colors.primary}) !important;
      background-repeat: repeat, no-repeat, no-repeat, no-repeat !important;
      animation: homepageShapeTravel 8s ease-in-out infinite, homepageShapeTurn 8s linear infinite !important;
      animation-delay: var(--home-flow-delay, 0s), var(--home-flow-delay, 0s) !important;
      animation-play-state: paused, paused !important;
    }
    .homepage-presentation [data-home-gradient="cta"]:not([aria-pressed="false"]) {
      background-image: ${GRAIN}, radial-gradient(ellipse at 25% 50%, ${theme.colors.primary}, transparent 44.2%),
        radial-gradient(ellipse at 75% 50%, ${theme.colors.secondary}, transparent 42.25%),
        linear-gradient(115deg, ${theme.colors.primary}, ${theme.colors.secondary}, ${theme.colors.primary}) !important;
      background-repeat: repeat, no-repeat, no-repeat, no-repeat !important;
    }
    .homepage-presentation [data-home-gradient="text"] {
      background-image: ${GRAIN}, linear-gradient(110deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 19.5%, ${theme.colors.primary} 35.75%, ${theme.colors.secondary} 52%, ${theme.colors.primary} 65%, ${theme.colors.secondary} 84.5%, ${theme.colors.primary} 100%) !important;
      background-size: 193px 193px, 240% 100% !important;
      background-repeat: repeat, no-repeat !important;
      animation-name: homepageTextTravel !important;
    }
    .homepage-presentation [data-home-gradient="border"] {
      /* Keep the neutral inner card surface stationary. */
      background-image: linear-gradient(145deg, #0b0c0d, #08090a), ${GRAIN}, ${theme.colors.gradientPrimary} !important;
      background-origin: padding-box, border-box, border-box !important;
      background-clip: padding-box, border-box, border-box !important;
      background-size: 100% 100%, 193px 193px, 240% 100% !important;
      background-repeat: no-repeat, repeat, no-repeat !important;
      animation-name: homepageBrandBorderFlow !important;
    }
    @keyframes homepageShapeTurn {
      from { --homepage-flow-angle: 0deg; }
      to { --homepage-flow-angle: 360deg; }
    }
    @keyframes homepageShapeTravel {
      0%, 100% { background-position: 0 0, 0% 20%, 100% 80%, 50% 50%; background-size: 193px 193px, 165% 140%, 150% 175%, 100% 100%; }
      25% { background-position: 0 0, 85% 0%, 20% 100%, 50% 50%; background-size: 193px 193px, 145% 180%, 180% 140%, 100% 100%; }
      50% { background-position: 0 0, 100% 85%, 0% 25%, 50% 50%; background-size: 193px 193px, 180% 150%, 140% 180%, 100% 100%; }
      75% { background-position: 0 0, 20% 100%, 80% 0%, 50% 50%; background-size: 193px 193px, 150% 175%, 175% 145%, 100% 100%; }
    }
    @keyframes homepageColorTravel {
      0%, 100% { background-position: 0 0, 0% 25%, 100% 75%, 50% 50%; background-size: 193px 193px, 170% 140%, 160% 160%, 100% 100%; }
      33% { background-position: 0 0, 100% 75%, 40% 0%, 50% 50%; background-size: 193px 193px, 150% 160%, 180% 140%, 100% 100%; }
      66% { background-position: 0 0, 40% 100%, 0% 80%, 50% 50%; background-size: 193px 193px, 185% 140%, 150% 170%, 100% 100%; }
    }
    @keyframes homepageTextTravel {
      0%, 100% { background-position: 0 0, 0% 50%; }
      50% { background-position: 0 0, 100% 50%; }
    }
    @keyframes homepageBrandBorderFlow {
      0%, 100% { background-position: 0 0, 0 0, 0% 50%; }
      50% { background-position: 0 0, 0 0, 100% 50%; }
    }
    @media (prefers-reduced-motion: reduce) {
      .homepage-presentation [data-home-gradient] { animation-play-state: paused !important; }
    }
  `}</style>;
};
